import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, withCtx as _withCtx, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  id: "main-navigation",
  class: "w-full h-full"
}
const _hoisted_2 = { class: "absolute left-5 bottom-5 sm:left-50px sm:bottom-50px flex items-center gap-10" }
const _hoisted_3 = { class: "text-stone" }

import { computed, reactive } from 'vue'
import BasicButton from '../general/BasicButton.vue'
import { MJPSection } from './types'
import useLanguage from '@/composables/useLanguage'
import SpeakerIcon from '@/assets/svg/SpeakerIcon.vue'
import SpeakerIconMuted from '@/assets/svg/SpeakerIconMuted.vue'
import SpeakerIconOutline from '@/assets/svg/SpeakerIconOutline.vue'
import SpeakerIconOutlineMuted from '@/assets/svg/SpeakerIconOutlineMuted.vue'
import { useLanguageStore } from '@/store/language'
import { useEventBus } from '@vueuse/core'
import { useSettingsStore } from '@/store/settings'
import { ButtonVariant } from '../general/types'
import sectionAudio from '@/assets/data/mjp/section-audio.js'
import GearIcon from '@/assets/svg/GearIcon.vue'
import ChevronDown from '@/assets/svg/ChevronDown.vue'

interface Props {
  currentSection: MJPSection
  currentSectionHasLoaded: boolean
  currentPage: number
  lastSection: MJPSection
  isMuted: boolean
  settingsAreOpen: boolean
}


export default /*#__PURE__*/_defineComponent({
  __name: 'MainNavigation',
  props: {
    currentSection: null,
    currentSectionHasLoaded: { type: Boolean },
    currentPage: null,
    lastSection: null,
    isMuted: { type: Boolean },
    settingsAreOpen: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  currentSection: MJPSection
  currentSectionHasLoaded: boolean
  currentPage: number
  lastSection: MJPSection
  isMuted: boolean
  settingsAreOpen: boolean
}


const interfaceText = useLanguage('interface')
const languageStore = useLanguageStore()
const settingsStore = useSettingsStore()
const sectionChangedBus = useEventBus('section-changed')
const setAudioMutedBus = useEventBus('set-audio-muted')
const settingsOpenedBus = useEventBus('settings-opened')
const sectionPageBus = useEventBus('section-page-bus')
const windowSize = {
  x: window.innerWidth,
  y: window.innerHeight,
}
const state = reactive({
  aspectRatio: window.innerWidth / window.innerHeight,
})

const updateWindowSize = () => {
  windowSize.x = window.innerWidth
  windowSize.y = window.innerHeight
}

const displaySection = computed(() => {
  if (props.currentSectionHasLoaded) {
    return props.currentSection
  } else {
    return props.lastSection
  }
})

const notFrontMatter = computed(() => {
  return displaySection.value != MJPSection.Title
})

const notEndMatter = computed(() => {
  return (
    displaySection.value != MJPSection.ProjectSummary &&
    displaySection.value != MJPSection.ProjectCredits
  )
})

const beforeTour = computed(() => {
  switch (displaySection.value) {
    case MJPSection.History:
    case MJPSection.IntentToHeal:
    case MJPSection.WatershedContext:
      return true

    default:
      return false
  }
})

const duringTour = computed(() => {
  switch (displaySection.value) {
    case MJPSection.WaterTreatmentFacility:
    case MJPSection.Wetlands:
    case MJPSection.Lakes:
    case MJPSection.StreamBridgeAndTrail:
    case MJPSection.SplashPad:
    case MJPSection.CommunityCenter:
      return true

    default:
      return false
  }
})

const skipButtonText = computed(() => {
  if (beforeTour.value) {
    return interfaceText.value.skipToTour
  } else if (displaySection.value == MJPSection.InvitationToExplore) {
    return interfaceText.value.skipToSummary
  } else if (duringTour.value) {
    return interfaceText.value.skipToSummary
  } else {
    return interfaceText.value.returnToFieldKit
  }
})

const toggleLanguage = (evt) => {
  languageStore.toggleLanguage()
}

const muteAudio = (evt) => {
  setAudioMutedBus.emit(true)
}

const unmuteAudio = (evt) => {
  setAudioMutedBus.emit(false)
}

const toggleMuteAudio = (evt) => {
  const newMuteState = !props.isMuted
  setAudioMutedBus.emit(newMuteState)
}

const handleClickBackToStartBtn = (evt) => {
  sectionChangedBus.emit({
    section: MJPSection.Title,
    pageNumber: -1,
    forward: true,
    lastSection: props.currentSection,
  })
}

const handleClickSkipBtn = (evt) => {
  if (beforeTour.value) {
    sectionChangedBus.emit({
      section: MJPSection.InvitationToExplore,
      pageNumber: 0,
      forward: true,
      lastSection: props.currentSection,
    })
  } else if (
    duringTour.value ||
    displaySection.value == MJPSection.InvitationToExplore
  ) {
    sectionChangedBus.emit({
      section: MJPSection.ProjectCredits,
      pageNumber: -1,
      forward: true,
      lastSection: props.currentSection,
    })
  } else {
    window.open('https://fieldkit.acceleratela.org/primer', '_self')
  }
}

const handleClickNextBtn = (evt) => {
  if (props.currentSection == MJPSection.History) {
    sectionChangedBus.emit({
      section: MJPSection.IntentToHeal,
      pageNumber: -1,
      forward: true,
      lastSection: props.currentSection,
    })
  }
}

const audioControl = computed(() => {
  if (settingsStore.highContrast) {
    return props.isMuted ? SpeakerIconOutlineMuted : SpeakerIconOutline
  } else {
    return props.isMuted ? SpeakerIconMuted : SpeakerIcon
  }
})

const lowerLeftButtonVariant = computed(() => {
  if (
    props.currentSection == MJPSection.WatershedContext &&
    props.currentPage == 0
  ) {
    return ButtonVariant.DarkOutline
  } else {
    return ButtonVariant.LightOutline
  }
})

const lowerRightButtonVariant = computed(() => {
  if (props.currentSection == MJPSection.WatershedContext) {
    if (state.aspectRatio >= 1.59 || props.currentPage > 0) {
      return ButtonVariant.LightOutline
    } else {
      return ButtonVariant.DarkOutline
    }
  } else {
    return ButtonVariant.LightOutline
  }
})

const handleResize = () => {
  updateWindowSize()
  state.aspectRatio = window.innerWidth / window.innerHeight
}

const handleClickSettingsBtn = () => {
  settingsOpenedBus.emit()
}

return (_ctx: any,_cache: any) => {
  const _directive_resize = _resolveDirective("resize")!

  return _withDirectives((_openBlock(), _createElementBlock("nav", _hoisted_1, [
    (
        _unref(sectionAudio)[props.currentSection].ambience ||
        props.currentSection == _unref(MJPSection).Settings
      )
      ? (_openBlock(), _createBlock(BasicButton, {
          key: 0,
          id: "audio-control",
          onClick: toggleMuteAudio,
          class: "absolute top-5 left-5 sm:top-50px sm:left-50px cursor-pointer",
          variant: _unref(ButtonVariant).LightOutline,
          tabindex: "500",
          "aria-label": _unref(interfaceText).muteAudio,
          "aria-pressed": props.isMuted
        }, {
          icon: _withCtx(() => [
            (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(audioControl)), { id: "audio-control" }))
          ]),
          _: 1
        }, 8, ["variant", "aria-label", "aria-pressed"]))
      : _createCommentVNode("", true),
    (!props.settingsAreOpen)
      ? (_openBlock(), _createBlock(BasicButton, {
          key: 1,
          id: "mobile-settings-control",
          onClick: handleClickSettingsBtn,
          class: "absolute top-5 right-5 sm:hidden",
          variant: _unref(ButtonVariant).LightOutline,
          tabindex: "501",
          "aria-label": _unref(interfaceText).settings
        }, {
          icon: _withCtx(() => [
            _createVNode(GearIcon)
          ]),
          _: 1
        }, 8, ["variant", "aria-label"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_2, [
      (_unref(notFrontMatter) && !props.settingsAreOpen)
        ? (_openBlock(), _createBlock(BasicButton, {
            key: 0,
            id: "back-to-start-btn",
            tabindex: "502",
            text: _unref(interfaceText).backToStart,
            variant: _unref(lowerLeftButtonVariant),
            onClick: handleClickBackToStartBtn
          }, null, 8, ["text", "variant"]))
        : _createCommentVNode("", true),
      (!props.settingsAreOpen)
        ? (_openBlock(), _createBlock(BasicButton, {
            key: 1,
            id: "settings-control",
            class: "hidden sm:flex",
            tabindex: "503",
            text: _unref(interfaceText).settings,
            variant: _unref(lowerLeftButtonVariant),
            onClick: handleClickSettingsBtn
          }, {
            icon: _withCtx(() => [
              _createVNode(GearIcon)
            ]),
            _: 1
          }, 8, ["text", "variant"]))
        : _createCommentVNode("", true)
    ]),
    (_unref(notEndMatter))
      ? (_openBlock(), _createElementBlock("button", {
          key: 2,
          tabindex: "504",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(sectionPageBus).emit(1))),
          class: "border-solid border-2 border-stone font-sans text-white text-base font-medium absolute hidden lg:flex left-2/4 bottom-30px w-200px -ml-100px flex-col items-center gap-3 bg-black bg-opacity-40 p-3 pb-4 rounded-xl"
        }, [
          _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(interfaceText).scrollInstruction), 1),
          _createVNode(ChevronDown, { class: "animate-bounce" })
        ]))
      : _createCommentVNode("", true),
    (_unref(notFrontMatter) && !props.settingsAreOpen)
      ? (_openBlock(), _createBlock(BasicButton, {
          key: 3,
          id: "skip-btn",
          tabindex: "505",
          class: "absolute right-5 bottom-5 hidden xxs:block sm:right-50px sm:bottom-50px",
          text: _unref(skipButtonText),
          variant: _unref(lowerRightButtonVariant),
          onClick: handleClickSkipBtn
        }, null, 8, ["text", "variant"]))
      : _createCommentVNode("", true)
  ])), [
    [_directive_resize, handleResize]
  ])
}
}

})